import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
    Plus,
    MoreVertical,
    Search,
    Loader2,
    CheckCircle2,
    Trash2,
    Pencil,
    Building2,
    Clock,
    PlayCircle
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

import { TaskCard } from '../components/tasks/TaskCard';
import { useTranslation } from '../i18n';

export default function TasksPage() {
  const { t } = useTranslation();
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const [searchQuery, setSearchQuery] = useState('');
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [errors, setErrors] = useState({});
    const [newTask, setNewTask] = useState({
        title: '',
        description: '',
        status: 'todo',
        deal_id: '',
        assignee_ids: []
    });
    const [editingTask, setEditingTask] = useState(null);
    const [isTaskModalEditMode, setIsTaskModalEditMode] = useState(false);

    // Data Fetching
    const { data: tasks = [], isLoading: loadingTasks } = useQuery({
        queryKey: ['tasks'],
        queryFn: async () => {
            const res = await api.get('/tasks');
            return Array.isArray(res) ? res : (res?.data || []);
        }
    });

    const { data: deals = [] } = useQuery({
        queryKey: ['deals'],
        queryFn: async () => {
            const res = await api.get('/deals');
            return Array.isArray(res) ? res : (res?.data || []);
        }
    });

    const { data: teamMembers = [] } = useQuery({
        queryKey: ['team'],
        queryFn: async () => {
            const res = await api.get('/team');
            return Array.isArray(res) ? res : (res?.data || []);
        }
    });

    // Mutations
    const createTaskMutation = useMutation({
        mutationFn: async (taskData) => {
            // Convert empty string deal_id to null for the backend
            const payload = { ...taskData };
            if (!payload.deal_id) payload.deal_id = null;

            return await api.post('/tasks', payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['tasks']);
            toast.success("Task created successfully");
            setIsCreateOpen(false);
            setErrors({});
            setNewTask({
                title: '',
                description: '',
                status: 'todo',
                deal_id: '',
                assignee_ids: []
            });
        },
        onError: (err) => {
            toast.error(err.response?.data?.detail || "Failed to create task");
        }
    });

    const updateStatusMutation = useMutation({
        mutationFn: async ({ taskId, status }) => {
            return await api.put(`/tasks/${taskId}`, { status });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['tasks']);
            toast.success("Task status updated");
        }
    });

    const updateTaskMutation = useMutation({
        mutationFn: async (taskData) => {
            const payload = { ...taskData };
            delete payload._id; // Ensure we don't send immutable id
            delete payload.created_at;
            delete payload.updated_at;
            if (!payload.deal_id) payload.deal_id = null;

            return await api.put(`/tasks/${taskData._id}`, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['tasks']);
            toast.success("Task updated successfully");
            setIsEditOpen(false);
            setEditingTask(null);
            setIsTaskModalEditMode(false);
        },
        onError: (err) => {
            toast.error(err.response?.data?.detail || "Failed to update task");
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => api.delete(`/tasks/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['tasks']);
            toast.success("Task deleted");
            setDeleteDialogOpen(false);
            setTaskToDelete(null);
        }
    });

    const handleDeleteClick = (taskId) => {
        setTaskToDelete(taskId);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (taskToDelete) {
            deleteMutation.mutate(taskToDelete);
        }
    };

    const handleStatusChange = (taskId, newStatus) => {
        updateStatusMutation.mutate({ taskId, status: newStatus });
    };

    const toggleAssignee = (memberId) => {
        setNewTask(prev => {
            const current = prev.assignee_ids || [];
            if (current.includes(memberId)) {
                return { ...prev, assignee_ids: current.filter(id => id !== memberId) };
            } else {
                return { ...prev, assignee_ids: [...current, memberId] };
            }
        });
    };

    const handleCreateTask = () => {
        const newErrors = {};
        if (!newTask.title.trim()) {
            newErrors.title = "Task title is required.";
        } else if (newTask.title.length > 100) {
            newErrors.title = "Task title cannot exceed 100 characters.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        createTaskMutation.mutate(newTask);
    };

    const handleEditTaskClick = (task) => {
        setEditingTask({ ...task });
        setErrors({});
        setIsTaskModalEditMode(false);
        setIsEditOpen(true);
    };

    const handleCloseTaskModal = (open) => {
        setIsEditOpen(open);
        if (!open) {
            setEditingTask(null);
            setIsTaskModalEditMode(false);
        }
    };

    const handleUpdateTask = () => {
        const newErrors = {};
        if (!editingTask.title.trim()) {
            newErrors.title = "Task title is required.";
        } else if (editingTask.title.length > 100) {
            newErrors.title = "Task title cannot exceed 100 characters.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        updateTaskMutation.mutate(editingTask);
    };

    const toggleEditAssignee = (memberId) => {
        setEditingTask(prev => {
            const current = prev.assignee_ids || [];
            if (current.includes(memberId)) {
                return { ...prev, assignee_ids: current.filter(id => id !== memberId) };
            } else {
                return { ...prev, assignee_ids: [...current, memberId] };
            }
        });
    };

    // Derived state
    const filteredTasks = tasks.filter(task =>
        task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const todoTasks = filteredTasks.filter(t => t.status === 'todo');
    const inProgressTasks = filteredTasks.filter(t => t.status === 'in_progress');
    const doneTasks = filteredTasks.filter(t => t.status === 'done');

    // Only Active/Owner can be assigned (exclude Invited to avoid orphaned invite ids after accept)
    const assignableMembers = teamMembers.filter(m => m.status !== 'Invited');

    if (loadingTasks) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">{t("tasks.tasks")}</h1>
                    <p className="text-muted-foreground mt-1 text-sm md:text-base">
                        Manage your organization's workflow and assignments.
                    </p>
                </div>

                <div className="flex w-full md:w-auto items-center gap-3">
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder={t("tasks.searchPlaceholder") || "Search tasks..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 bg-card focus-visible:ring-1"
                        />
                    </div>

                    <Button onClick={() => setIsCreateOpen(true)} className="gap-2">
                        <Plus className="h-4 w-4" />
                        Create Task
                    </Button>
                </div>
            </div>

            {!loadingTasks && (!tasks || tasks.length === 0) && (
                <div className="text-center py-16">
                    <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                        <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{t("tasks.noTasksYet")}</h3>
                    <p className="text-muted-foreground text-sm mb-4">Create tasks to track your work. Link them to deals, set priorities, and organize with a kanban board.</p>
                    <Button onClick={() => setIsCreateOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Task
                    </Button>
                </div>
            )}


            {/* Kanban Board */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start h-[calc(100vh-250px)]">
                {/* Column: To Do */}
                <div className="bg-muted/30 rounded-xl p-4 flex flex-col h-full max-h-full overflow-hidden border">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-semibold text-sm uppercase tracking-wider text-slate-500">
                            To Do <span className="ml-2 bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full text-xs">{todoTasks.length}</span>
                        </h2>
                    </div>
                    <div className="flex-1 overflow-y-auto pr-1 space-y-3 pb-2 custom-scrollbar">
                        {todoTasks.length === 0 ? (
                            <div className="text-center p-6 border-2 border-dashed rounded-lg text-muted-foreground text-sm">
                                {t("tasks.noTasks") || "No tasks in this column"}
                            </div>
                        ) : (
                            todoTasks.map(task => (
                                <div key={task._id} className="relative group">
                                    <TaskCard
                                        task={task}
                                        deals={deals}
                                        teamMembers={teamMembers}
                                        onStatusChange={handleStatusChange}
                                        onClick={() => handleEditTaskClick(task)}
                                    />
                                    <button
                                        onClick={() => handleDeleteClick(task._id)}
                                        className="absolute top-2 right-2 p-1.5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                                        title={t("tasks.deleteTask") || "Delete Task"}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Column: In Progress */}
                <div className="bg-muted/30 rounded-xl p-4 flex flex-col h-full max-h-full overflow-hidden border">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-semibold text-sm uppercase tracking-wider text-blue-500">
                            In Progress <span className="ml-2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">{inProgressTasks.length}</span>
                        </h2>
                    </div>
                    <div className="flex-1 overflow-y-auto pr-1 space-y-3 pb-2 custom-scrollbar">
                        {inProgressTasks.length === 0 ? (
                            <div className="text-center p-6 border-2 border-dashed rounded-lg text-muted-foreground text-sm flex flex-col items-center justify-center h-24">
                                <span className="text-xs">{t("tasks.dragTasksHere")}</span>
                            </div>
                        ) : (
                            inProgressTasks.map(task => (
                                <div key={task._id} className="relative group">
                                    <TaskCard
                                        task={task}
                                        deals={deals}
                                        teamMembers={teamMembers}
                                        onStatusChange={handleStatusChange}
                                        onClick={() => handleEditTaskClick(task)}
                                    />
                                    <button
                                        onClick={() => handleDeleteClick(task._id)}
                                        className="absolute top-2 right-2 p-1.5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                                        title={t("tasks.deleteTask") || "Delete Task"}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Column: Done */}
                <div className="bg-muted/30 rounded-xl p-4 flex flex-col h-full max-h-full overflow-hidden border">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-semibold text-sm uppercase tracking-wider text-green-500">
                            Done <span className="ml-2 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">{doneTasks.length}</span>
                        </h2>
                    </div>
                    <div className="flex-1 overflow-y-auto pr-1 space-y-3 pb-2 custom-scrollbar">
                        {doneTasks.length === 0 ? (
                            <div className="text-center p-6 border-2 border-dashed rounded-lg text-muted-foreground text-sm">
                                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-20" />
                                No completed tasks
                            </div>
                        ) : (
                            doneTasks.map(task => (
                                <div key={task._id} className="relative group">
                                    <TaskCard
                                        task={task}
                                        deals={deals}
                                        teamMembers={teamMembers}
                                        onStatusChange={handleStatusChange}
                                        onClick={() => handleEditTaskClick(task)}
                                    />
                                    <button
                                        onClick={() => handleDeleteClick(task._id)}
                                        className="absolute top-2 right-2 p-1.5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                                        title={t("tasks.deleteTask") || "Delete Task"}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Create Task Modal */}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
                    <DialogHeader className="p-6 pb-2 flex-shrink-0">
                        <DialogTitle>{t("tasks.createNewTask")}</DialogTitle>
                        <DialogDescription>
                            Assign a task to your team. You can attach it to a specific deal.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto px-6 py-2">
                        <div className="grid gap-4 py-2">
                            <div className="grid gap-2">
                                <Label htmlFor="title">Task Title <span className="text-destructive">*</span></Label>
                                <Input
                                    id="title"
                                    placeholder={t("tasks.ph.title") || "e.g. Review legal documents"}
                                    value={newTask.title}
                                    onChange={(e) => {
                                        setNewTask({ ...newTask, title: e.target.value });
                                        if (errors.title) setErrors({ ...errors, title: null });
                                    }}
                                    className={errors.title ? "border-red-500" : ""}
                                />
                                {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
                                <p className="text-xs text-muted-foreground text-right">{newTask.title.length}/100</p>
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="description">{t("tasks.descriptionOptional")}</Label>
                                <Textarea
                                    id="description"
                                    placeholder={t("tasks.ph.description") || "Add more context to this task..."}
                                    className="min-h-[100px]"
                                    value={newTask.description}
                                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                                />
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="deal">{t("tasks.relatedDealOptional")}</Label>
                                <Select
                                    value={newTask.deal_id}
                                    onValueChange={(val) => setNewTask({ ...newTask, deal_id: val === "none" ? "" : val })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t("tasks.selectDeal") || "Select a deal to attach"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">{t("tasks.noDealAttached")}</SelectItem>
                                        {deals.map(deal => (
                                            <SelectItem key={deal._id} value={deal._id}>{deal.title}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid gap-2">
                                <Label>{t("tasks.assignees")}</Label>
                                <div className="grid grid-cols-2 gap-2 border rounded-md p-3 max-h-40 overflow-y-auto bg-muted/40">
                                    {assignableMembers.length === 0 ? (
                                        <p className="text-sm text-muted-foreground col-span-2">{t("tasks.noTeamMembersAvailable")}</p>
                                    ) : (
                                        assignableMembers.map(member => (
                                            <div
                                                key={member._id}
                                                className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${newTask.assignee_ids.includes(member._id)
                                                    ? 'bg-primary/10 border border-primary/20'
                                                    : 'hover:bg-muted/80 border border-transparent'
                                                    }`}
                                                onClick={() => toggleAssignee(member._id)}
                                            >
                                                <div className="h-6 w-6 rounded-full bg-background flex items-center justify-center border">
                                                    <span className="text-[10px] font-medium text-foreground">
                                                        {member.name.substring(0, 2).toUpperCase()}
                                                    </span>
                                                </div>
                                                <span className="text-sm truncate w-full" title={member.name}>
                                                    {member.name}
                                                    {member._id === (user?._id || user?.id) ? " (You)" : ""}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
                        <DialogFooter className="pt-2">
                            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>{t("tasks.cancel")}</Button>
                            <Button
                                onClick={handleCreateTask}
                                disabled={createTaskMutation.isPending}
                            >
                                {createTaskMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Create Task
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* View / Edit Task Modal */}
            <Dialog open={isEditOpen} onOpenChange={handleCloseTaskModal}>
                <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
                    {editingTask && (
                        <>
                            <DialogHeader className="p-6 pb-2 flex-shrink-0">
                                <DialogTitle className="pr-8">
                                    {isTaskModalEditMode ? 'Edit Task' : editingTask.title || 'Task'}
                                </DialogTitle>
                                <DialogDescription>
                                    {isTaskModalEditMode
                                        ? 'Update task details, assignees, or associated deal.'
                                        : 'Task details'}
                                </DialogDescription>
                            </DialogHeader>

                            {!isTaskModalEditMode ? (
                                /* View mode: read-only */
                                <>
                                    <div className="flex-1 overflow-y-auto px-6 py-2">
                                        <div className="space-y-5 py-2">
                                            {editingTask.description && (
                                                <div>
                                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t("tasks.description")}</p>
                                                    <p className="text-sm text-foreground whitespace-pre-wrap rounded-md bg-muted/50 p-3 border">
                                                        {editingTask.description}
                                                    </p>
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t("tasks.status")}</p>
                                                {(() => {
                                                    const statusInfo = { todo: { label: 'To Do', icon: Clock, color: 'bg-slate-500/10 text-slate-600 border-slate-200' }, in_progress: { label: 'In Progress', icon: PlayCircle, color: 'bg-blue-500/10 text-blue-700 border-blue-200' }, done: { label: 'Completed', icon: CheckCircle2, color: 'bg-green-500/10 text-green-700 border-green-200' } }[editingTask.status] || { label: editingTask.status, icon: Clock, color: 'bg-muted text-muted-foreground' };
                                                    const StatusIcon = statusInfo.icon;
                                                    return (
                                                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-sm font-medium ${statusInfo.color}`}>
                                                            <StatusIcon className="h-3.5 w-3.5" />
                                                            {statusInfo.label}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t("tasks.relatedDeal")}</p>
                                                {editingTask.deal_id ? (
                                                    <div className="flex items-center gap-2 text-sm rounded-md bg-muted/50 px-3 py-2 border w-fit">
                                                        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                                                        <span className="truncate max-w-[240px]">
                                                            {deals.find(d => d._id === editingTask.deal_id)?.title || 'Deal'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">{t("tasks.noDealAttached")}</p>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t("tasks.assignees")}</p>
                                                {(editingTask.assignee_ids && editingTask.assignee_ids.length > 0) ? (
                                                    <div className="flex flex-wrap gap-2">
                                                        {teamMembers
                                                            .filter(m => editingTask.assignee_ids.includes(m._id))
                                                            .map(member => (
                                                                <div
                                                                    key={member._id}
                                                                    className="flex items-center gap-2 rounded-md border bg-muted/40 px-2.5 py-1.5 text-sm"
                                                                >
                                                                    <div className="h-6 w-6 rounded-full bg-background flex items-center justify-center border shrink-0">
                                                                        <span className="text-[10px] font-medium text-foreground">
                                                                            {member.name?.substring(0, 2).toUpperCase() || '?'}
                                                                        </span>
                                                                    </div>
                                                                    <span className="truncate max-w-[140px]" title={member.name}>
                                                                        {member.name}
                                                                        {member._id === (user?._id || user?.id) ? ' (You)' : ''}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">{t("tasks.noAssignees")}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
                                        <DialogFooter className="pt-2 gap-2 sm:gap-2">
                                            <Button variant="outline" onClick={() => handleCloseTaskModal(false)}>
                                                Close
                                            </Button>
                                            <Button onClick={() => setIsTaskModalEditMode(true)} className="gap-1.5">
                                                <Pencil className="h-4 w-4" />
                                                Edit
                                            </Button>
                                        </DialogFooter>
                                    </div>
                                </>
                            ) : (
                                /* Edit mode: form */
                                <>
                                    <div className="flex-1 overflow-y-auto px-6 py-2">
                                        <div className="grid gap-4 py-2">
                                            <div className="grid gap-2">
                                                <Label htmlFor="edit-title">Task Title <span className="text-destructive">*</span></Label>
                                                <Input
                                                    id="edit-title"
                                                    value={editingTask.title}
                                                    onChange={(e) => {
                                                        setEditingTask({ ...editingTask, title: e.target.value });
                                                        if (errors.title) setErrors({ ...errors, title: null });
                                                    }}
                                                    className={errors.title ? "border-red-500" : ""}
                                                />
                                                {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
                                                <p className="text-xs text-muted-foreground text-right">{editingTask.title.length}/100</p>
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="edit-description">{t("tasks.description")}</Label>
                                                <Textarea
                                                    id="edit-description"
                                                    className="min-h-[100px]"
                                                    value={editingTask.description}
                                                    onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                                                />
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="edit-deal">{t("tasks.relatedDealOptional")}</Label>
                                                <Select
                                                    value={editingTask.deal_id || "none"}
                                                    onValueChange={(val) => setEditingTask({ ...editingTask, deal_id: val === "none" ? "" : val })}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t("tasks.selectDeal") || "Select a deal to attach"} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="none">{t("tasks.noDealAttached")}</SelectItem>
                                                        {deals.map(deal => (
                                                            <SelectItem key={deal._id} value={deal._id}>{deal.title}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="grid gap-2">
                                                <Label>{t("tasks.assignees")}</Label>
                                                <div className="grid grid-cols-2 gap-2 border rounded-md p-3 max-h-40 overflow-y-auto bg-muted/40">
                                                    {assignableMembers.length === 0 ? (
                                                        <p className="text-sm text-muted-foreground col-span-2">{t("tasks.noTeamMembersAvailable")}</p>
                                                    ) : (
                                                        assignableMembers.map(member => (
                                                            <div
                                                                key={member._id}
                                                                className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${(editingTask.assignee_ids || []).includes(member._id)
                                                                    ? 'bg-primary/10 border border-primary/20'
                                                                    : 'hover:bg-muted/80 border border-transparent'
                                                                    }`}
                                                                onClick={() => toggleEditAssignee(member._id)}
                                                            >
                                                                <div className="h-6 w-6 rounded-full bg-background flex items-center justify-center border">
                                                                    <span className="text-[10px] font-medium text-foreground">
                                                                        {member.name.substring(0, 2).toUpperCase()}
                                                                    </span>
                                                                </div>
                                                                <span className="text-sm truncate w-full" title={member.name}>
                                                                    {member.name}
                                                                    {member._id === (user?._id || user?.id) ? " (You)" : ""}
                                                                </span>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
                                        <DialogFooter className="pt-2">
                                            <Button variant="outline" onClick={() => { setIsTaskModalEditMode(false); setErrors({}); }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleUpdateTask}
                                                disabled={updateTaskMutation.isPending}
                                            >
                                                {updateTaskMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                                Save Changes
                                            </Button>
                                        </DialogFooter>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("tasks.areYouAbsolutelySure")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the task and remove its data from our servers.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("tasks.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

        </div>
    );
}
